import React, { useState, useRef, useContext } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import { MenuList } from './menu-list';

const getStyles = (components, { palette = {} }) => ({
  container: {
    marginLeft: 'auto',
    marginRight: 12,
    width: 44,
    height: 38,
  },
  menuIcon: {
    ...components.defaultButton,
    display: 'flex',
    flexDirection: 'column',
    border: 'none',
    background: 'none',
    padding: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    justifyContent: 'center',
  },
  menuIconPart: {
    height: 2,
    backgroundColor: palette.outline,
    margin: '2px auto',
    width: 20,
  },
  navContainer: {
    listStyle: 'none',
    padding: 'unset',

    // center align all menu items
    '& li a': {
      textAlign: 'center',
      width: '100%',
      marginBottom: 36,
      fontSize: 28,
      textDecoration: 'solid',
      textDecorationLine: 'underline',
    },
  },
  navItem: {
    fontSize: 20,
    lineHeight: '23px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: 20,
  },
  navLink: {
    textDecoration: 'none',
    ...components.navLink,
  },
});

// Note: Take care of the Panel Component, for accesibility when the panel is closed,
// fluentUI is designed to keep the browser focus back to the element that activates the panel,
// which will cause a scroll to make the header show again. This has a conflict with what we
// want to do because we want the "scrollTarget" to be shown in the view. Use either of
// the following methods to solve this problem:
// - Add props "popupProps={{ onRestoreFocus: () => {} }}",
//   this will override the default behavior in Popup.
// - Add props "isHiddenOnDismiss", this will keep the Popup hidden rather than destroy
//   and thus the clear effects function of "useRestoreFocus" hooks in Popup.tsx won't be executed.
export const UnStyledHamburgerMenu = ({
  classes,
  items,
  top,
  left,
  width,
  triggerRecalculation,
}) => {
  const [menuExpanded, setMenuExpanded] = useState(false);
  const scrollTarget = useRef();

  const i18n = useContext(I18nContext);

  const closePanel = () => {
    setMenuExpanded(false);
  };
  const handleClick = (e, id) => {
    scrollTarget.current = id;
    closePanel();
  };

  return (
    <div className={classes.container} >
      <button
        className={classnames([classes.menuIcon, 'smart-page-hamburger-button'])}
        onClick={() => {
          triggerRecalculation();
          setMenuExpanded(prev => !prev);
        }}
        aria-expanded={menuExpanded}
        aria-label={i18n.getString(_TL_('Navigation pane'))}
        aria-haspopup
      >
        <span className={classes.menuIconPart} />
        <span className={classes.menuIconPart} />
        <span className={classes.menuIconPart} />
        <span className={classes.menuIconPart} />
      </button>
      <Panel
        isHiddenOnDismiss
        className="smart-page-header-navigation-pane"
        isBlocking={false}
        type={PanelType.smallFixedFar}
        isOpen={menuExpanded}
        onDismiss={closePanel}
        onDismissed={() => {
          if (scrollTarget.current) {
            document.getElementById(scrollTarget.current).scrollIntoView(true);
            scrollTarget.current = null;
          }
        }}
        closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
        styles={{
          root: {
            left,
            width,
            top,
          },
          main: {
            // hamburger menu needs to cover parent container 100%
            width: '100% !important',
          },
        }}
      >
        <MenuList
          className={classes.navContainer}
          navItemClassName={classes.navItem}
          navAnchorClassName={classes.navLink}
          items={items}
          onClick={handleClick}
        />
      </Panel>
    </div>
  );
};

UnStyledHamburgerMenu.propTypes = {
  classes: PropTypes.shape({
    menuIcon: PropTypes.string.isRequired,
    menuIconPart: PropTypes.string.isRequired,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    targetId: PropTypes.string.isRequired,
  })).isRequired,
  top: PropTypes.number,
  left: PropTypes.number,
  width: PropTypes.number,
  triggerRecalculation: PropTypes.func.isRequired,
};

UnStyledHamburgerMenu.defaultProps = {
  top: 0,
  left: 0,
  width: 0,
};

export const HamburgerMenu = withDefaultStyles(
  UnStyledHamburgerMenu,
  getStyles
);
